import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Header from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Save, Sparkles, Ban, Loader2, BadgeCheck, CircleSlash, Clock, RotateCcw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyPlan = () => ({
  plan_id: '',
  name: '',
  description: '',
  price: '',
  interval: 'month',
  features: [''],
  active: true,
  sort_order: 100,
});

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtINR = (n) => {
  if (n === undefined || n === null || Number.isNaN(Number(n)) || Number(n) === 0) return '—';
  return `\u20B9${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const STATUS_META = {
  active: { label: 'Active', icon: BadgeCheck, cls: 'bg-green-50 text-green-700 ring-green-200' },
  cancelled: { label: 'Cancelled', icon: CircleSlash, cls: 'bg-gray-100 text-gray-700 ring-gray-200' },
  refunded: { label: 'Refunded', icon: RotateCcw, cls: 'bg-amber-50 text-amber-700 ring-amber-200' },
  paused: { label: 'Paused', icon: Clock, cls: 'bg-blue-50 text-blue-700 ring-blue-200' },
};

const PAY_STATUS_CLS = {
  paid: 'bg-green-50 text-green-700 ring-green-200',
  pending: 'bg-amber-50 text-amber-700 ring-amber-200',
  failed: 'bg-red-50 text-red-700 ring-red-200',
  unknown: 'bg-gray-100 text-gray-600 ring-gray-200',
};

const AdminSubscriptions = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  // Subscriptions-tab state
  const [tab, setTab] = useState('plans');
  const [subs, setSubs] = useState([]);
  const [subCounts, setSubCounts] = useState({ total: 0, active: 0, cancelled: 0, refunded: 0 });
  const [subsLoading, setSubsLoading] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null); // sub object
  const [cancelReason, setCancelReason] = useState('');
  const [cancelRefund, setCancelRefund] = useState(true);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  useEffect(() => { fetchPlans(); }, []);
  useEffect(() => { if (tab === 'subs') fetchSubs(); }, [tab]);

  const fetchSubs = async () => {
    setSubsLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/subscriptions`, { withCredentials: true });
      setSubs(data.subscriptions || []);
      setSubCounts(data.counts || { total: 0, active: 0, cancelled: 0, refunded: 0 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load subscriptions');
    } finally { setSubsLoading(false); }
  };

  const openCancel = (sub) => {
    setCancelTarget(sub);
    setCancelReason('');
    setCancelRefund(Boolean(sub.razorpay_payment_id && sub.amount > 0));
  };
  const closeCancel = () => { if (!cancelSubmitting) { setCancelTarget(null); setCancelReason(''); } };

  const submitCancel = async () => {
    if (!cancelTarget) return;
    const reason = cancelReason.trim();
    if (!reason) { toast.error('Please enter a cancellation reason'); return; }
    setCancelSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/subscriptions/${cancelTarget.subscription_id}/cancel`,
        { reason, refund: cancelRefund },
        { withCredentials: true }
      );
      const emailNote = (data.emails_sent?.customer && data.emails_sent?.admin)
        ? ''
        : ' (some email notifications failed — check SMTP settings)';
      toast.success(data.message + emailNote);
      setCancelTarget(null);
      setCancelReason('');
      await fetchSubs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cancel failed');
    } finally { setCancelSubmitting(false); }
  };

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/subscriptions/plans`, { withCredentials: true });
      setPlans(data.plans || []);
    } catch {
      toast.error('Failed to load plans');
    } finally { setLoading(false); }
  };

  const openCreate = () => setEditing({ ...emptyPlan(), _isNew: true });
  const openEdit = (plan) => setEditing({ ...plan, features: plan.features?.length ? plan.features : [''], _isNew: false });

  const updateField = (field, value) => setEditing((p) => ({ ...p, [field]: value }));
  const updateFeature = (idx, value) => {
    const next = [...editing.features];
    next[idx] = value;
    setEditing({ ...editing, features: next });
  };
  const addFeatureRow = () => setEditing({ ...editing, features: [...editing.features, ''] });
  const removeFeatureRow = (idx) => setEditing({ ...editing, features: editing.features.filter((_, i) => i !== idx) });

  const savePlan = async () => {
    if (!editing.name?.trim()) { toast.error('Plan name is required'); return; }
    const price = Number(editing.price);
    if (isNaN(price) || price <= 0) { toast.error('Price must be a positive number'); return; }
    setSaving(true);
    const payload = {
      plan_id: editing.plan_id || undefined,
      name: editing.name.trim(),
      description: editing.description?.trim() || '',
      price,
      interval: editing.interval || 'month',
      features: (editing.features || []).map((f) => f.trim()).filter(Boolean),
      active: !!editing.active,
      sort_order: Number(editing.sort_order) || 100,
    };
    try {
      if (editing._isNew) {
        await axios.post(`${API}/api/admin/subscriptions/plans`, payload, { withCredentials: true });
        toast.success('Plan created');
      } else {
        await axios.put(`${API}/api/admin/subscriptions/plans/${editing.plan_id}`, payload, { withCredentials: true });
        toast.success('Plan updated');
      }
      setEditing(null);
      fetchPlans();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const deletePlan = async (planId, name) => {
    if (!window.confirm(`Delete plan "${name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/admin/subscriptions/plans/${planId}`, { withCredentials: true });
      toast.success('Plan deleted');
      fetchPlans();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  if (loading) {
    return (<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" /></div>);
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <div className="container mx-auto px-4 py-10 pt-16 max-w-5xl">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-3xl font-black font-heading flex items-center gap-2" data-testid="admin-subscriptions-heading">
              <Sparkles className="h-7 w-7 text-primary" /> Ritual Subscriptions
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Manage plans shown to customers, and review/cancel/refund individual subscriptions.</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="mb-6" data-testid="subs-tabs-list">
            <TabsTrigger value="plans" data-testid="subs-tab-plans">Plans</TabsTrigger>
            <TabsTrigger value="subs" data-testid="subs-tab-subscriptions">
              Subscriptions{subCounts.total ? ` (${subCounts.total})` : ''}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="plans" className="m-0">
            <div className="flex justify-end mb-4">
              <Button onClick={openCreate} className="rounded-full" data-testid="add-plan-btn">
                <Plus className="h-4 w-4 mr-1" /> New Plan
              </Button>
            </div>

            {plans.length === 0 ? (
          <div className="p-10 rounded-2xl bg-white border border-[#E0E0E0] text-center" data-testid="no-plans-state">
            <p className="text-sm text-muted-foreground mb-4">No subscription plans yet.</p>
            <Button onClick={openCreate}><Plus className="h-4 w-4 mr-1" /> Create First Plan</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {plans.map((plan) => (
              <div key={plan.plan_id} className="p-5 rounded-2xl bg-white border border-[#E0E0E0] flex flex-col" data-testid={`plan-card-${plan.plan_id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{plan.plan_id}</p>
                    <h3 className="font-heading font-semibold text-lg" data-testid={`plan-name-${plan.plan_id}`}>{plan.name}</h3>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${plan.active === false ? 'bg-gray-200 text-gray-600' : 'bg-primary/10 text-primary'}`}>
                    {plan.active === false ? 'Inactive' : 'Active'}
                  </span>
                </div>
                <p className="text-2xl font-bold text-primary mb-1">{'\u20B9'}{Number(plan.price).toFixed(0)}<span className="text-xs font-normal text-muted-foreground">/{plan.interval}</span></p>
                <p className="text-xs text-muted-foreground mb-3">{plan.description}</p>
                <ul className="text-xs text-muted-foreground space-y-1 mb-4 flex-1">
                  {(plan.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-1.5"><span className="text-primary">•</span> {f}</li>
                  ))}
                </ul>
                <div className="flex gap-2 mt-auto">
                  <Button size="sm" variant="outline" onClick={() => openEdit(plan)} data-testid={`edit-plan-${plan.plan_id}`}>
                    <Pencil className="h-3 w-3 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" className="text-destructive hover:bg-destructive/10" onClick={() => deletePlan(plan.plan_id, plan.name)} data-testid={`delete-plan-${plan.plan_id}`}>
                    <Trash2 className="h-3 w-3 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
          </TabsContent>

          <TabsContent value="subs" className="m-0">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <div className="text-sm">
                <span className="text-foreground font-bold">{subCounts.total}</span> <span className="text-muted-foreground">total</span>
                <span className="text-muted-foreground"> · </span>
                <span className="text-green-700 font-bold">{subCounts.active}</span> <span className="text-muted-foreground">active</span>
                <span className="text-muted-foreground"> · </span>
                <span className="text-gray-700 font-bold">{subCounts.cancelled}</span> <span className="text-muted-foreground">cancelled</span>
                <span className="text-muted-foreground"> · </span>
                <span className="text-amber-700 font-bold">{subCounts.refunded}</span> <span className="text-muted-foreground">refunded</span>
              </div>
              <Button size="sm" variant="outline" onClick={fetchSubs} disabled={subsLoading} data-testid="refresh-subs-btn">
                {subsLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null} Refresh
              </Button>
            </div>

            {subsLoading ? (
              <div className="p-10 rounded-2xl bg-white border border-[#E0E0E0] text-center text-muted-foreground" data-testid="subs-loading">
                <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading subscriptions…
              </div>
            ) : subs.length === 0 ? (
              <div className="p-10 rounded-2xl bg-white border border-[#E0E0E0] text-center" data-testid="no-subs-state">
                <p className="text-sm text-muted-foreground">No subscriptions yet. Customers' purchases will appear here.</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-[#E0E0E0] overflow-hidden" data-testid="admin-subs-table-wrap">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#FAFAFA] border-b border-[#E0E0E0]">
                      <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                        <th className="px-4 py-3">Customer</th>
                        <th className="px-4 py-3">Plan</th>
                        <th className="px-4 py-3 text-right">Amount</th>
                        <th className="px-4 py-3">Payment</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subs.map((s) => {
                        const meta = STATUS_META[s.status] || STATUS_META.cancelled;
                        const Icon = meta.icon;
                        const payCls = PAY_STATUS_CLS[s.payment_status] || PAY_STATUS_CLS.unknown;
                        const canCancel = s.status === 'active';
                        return (
                          <tr key={s.subscription_id} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA]" data-testid={`sub-row-${s.subscription_id}`}>
                            <td className="px-4 py-3 min-w-[180px]">
                              <div className="font-medium text-foreground">{s.user_name || '—'}</div>
                              <div className="text-xs text-muted-foreground break-all">{s.user_email || '—'}</div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="font-medium" data-testid={`sub-plan-${s.subscription_id}`}>{s.plan_name || s.plan_type || '—'}</div>
                              {s.plan_interval ? (<div className="text-[10px] uppercase tracking-wider text-muted-foreground">/{s.plan_interval}</div>) : null}
                            </td>
                            <td className="px-4 py-3 text-right font-bold text-primary tabular-nums" data-testid={`sub-amount-${s.subscription_id}`}>{fmtINR(s.amount)}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ring-1 ${payCls}`} data-testid={`sub-pay-${s.subscription_id}`}>
                                {s.payment_status || 'unknown'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ring-1 ${meta.cls}`} data-testid={`sub-status-${s.subscription_id}`}>
                                <Icon className="h-3 w-3" /> {meta.label}
                              </span>
                              {s.cancel_reason && (
                                <div className="text-[10px] text-muted-foreground mt-1 max-w-[180px] truncate" title={s.cancel_reason}>
                                  {s.cancel_reason}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(s.created_at)}</td>
                            <td className="px-4 py-3 text-right">
                              {canCancel ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={() => openCancel(s)}
                                  data-testid={`cancel-sub-${s.subscription_id}`}
                                >
                                  <Ban className="h-3 w-3 mr-1" /> Cancel
                                </Button>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing?._isNew ? 'New Plan' : `Edit · ${editing?.name}`}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs">Plan name</Label>
                  <Input value={editing.name} onChange={(e) => updateField('name', e.target.value)} placeholder="Monthly Ritual" data-testid="edit-plan-name" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Description</Label>
                  <Input value={editing.description} onChange={(e) => updateField('description', e.target.value)} placeholder="A curated box of 2 premium teas" data-testid="edit-plan-description" />
                </div>
                <div>
                  <Label className="text-xs">Price ({'\u20B9'})</Label>
                  <Input type="number" value={editing.price} onChange={(e) => updateField('price', e.target.value)} placeholder="999" data-testid="edit-plan-price" />
                </div>
                <div>
                  <Label className="text-xs">Billing interval</Label>
                  <select value={editing.interval} onChange={(e) => updateField('interval', e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="edit-plan-interval">
                    <option value="month">Monthly</option>
                    <option value="quarter">Quarterly</option>
                    <option value="year">Yearly</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Sort order (lower = earlier)</Label>
                  <Input type="number" value={editing.sort_order} onChange={(e) => updateField('sort_order', e.target.value)} placeholder="100" data-testid="edit-plan-sort" />
                </div>
                <div className="flex items-end gap-2">
                  <Switch checked={!!editing.active} onCheckedChange={(v) => updateField('active', v)} data-testid="edit-plan-active" />
                  <span className="text-xs text-muted-foreground pb-2">{editing.active ? 'Active (visible to customers)' : 'Inactive (hidden)'}</span>
                </div>
                {!editing._isNew && (
                  <div className="col-span-2">
                    <Label className="text-xs">Plan ID (read-only)</Label>
                    <Input value={editing.plan_id} disabled className="bg-muted" />
                  </div>
                )}
              </div>

              <div>
                <Label className="text-xs">Features</Label>
                <div className="space-y-2 mt-1">
                  {editing.features.map((f, i) => (
                    <div key={i} className="flex gap-2">
                      <Input value={f} onChange={(e) => updateFeature(i, e.target.value)} placeholder={`Feature ${i + 1}`} data-testid={`edit-plan-feature-${i}`} />
                      <Button size="icon" variant="ghost" className="h-9 w-9 text-destructive" onClick={() => removeFeatureRow(i)} disabled={editing.features.length <= 1} data-testid={`remove-feature-${i}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" onClick={addFeatureRow} className="w-full" data-testid="add-feature-btn">
                    <Plus className="h-3 w-3 mr-1" /> Add feature
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="cancel-plan-btn">Cancel</Button>
            <Button onClick={savePlan} disabled={saving} data-testid="save-plan-btn">
              <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : 'Save Plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!cancelTarget} onOpenChange={(v) => !v && closeCancel()}>
        <DialogContent className="max-w-md" data-testid="cancel-sub-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700">Cancel subscription</DialogTitle>
            <DialogDescription asChild>
              <div className="text-sm space-y-2">
                {cancelTarget && (
                  <div className="rounded-lg border border-red-100 bg-red-50/40 p-3">
                    <div className="font-semibold text-foreground">{cancelTarget.plan_name || cancelTarget.plan_type || 'Subscription'}</div>
                    <div className="text-muted-foreground">{cancelTarget.user_name} · {cancelTarget.user_email}</div>
                    <div className="text-xs font-mono text-muted-foreground mt-1">{cancelTarget.subscription_id}</div>
                  </div>
                )}
                <p>This will set the subscription status to <b>cancelled</b>{cancelRefund ? ' / refunded' : ''} and email the customer (and the admin inbox) with your reason.</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          {cancelTarget && (
            <div className="space-y-3">
              <div>
                <Label htmlFor="cancel-reason" className="text-xs">Reason (sent to customer)</Label>
                <textarea
                  id="cancel-reason"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows={3}
                  className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  placeholder="Why is this subscription being cancelled?"
                  data-testid="cancel-reason-input"
                />
              </div>
              {cancelTarget.razorpay_payment_id && cancelTarget.amount > 0 ? (
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={cancelRefund}
                    onCheckedChange={(v) => setCancelRefund(!!v)}
                    data-testid="cancel-refund-checkbox"
                  />
                  <span>
                    Also refund <b>{fmtINR(cancelTarget.amount)}</b> via Razorpay
                    <span className="block text-xs text-muted-foreground">Reverses payment <code className="text-[10px]">{cancelTarget.razorpay_payment_id}</code>. Customer will receive a refund-confirmation email.</span>
                  </span>
                </label>
              ) : (
                <p className="text-xs text-muted-foreground">No Razorpay payment is linked to this subscription, so an automatic refund isn't possible from here.</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeCancel} disabled={cancelSubmitting} data-testid="cancel-sub-dialog-back">Back</Button>
            <Button
              onClick={submitCancel}
              disabled={cancelSubmitting || !cancelReason.trim()}
              className="bg-red-600 hover:bg-red-700"
              data-testid="cancel-sub-confirm"
            >
              {cancelSubmitting ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Processing…</>) : (cancelRefund ? 'Cancel & Refund' : 'Cancel Subscription')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSubscriptions;
